import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, ConfigSettings } from '@shared/config';
import { DATA_API_URL } from '@data-api/config/data-api-url.config';
import { Observable } from 'rxjs';
import { MetadataQueryRequest } from '@data-api/models/metadata-query/metadata-query-request.model';
import { MetadataQueryGenericResponse } from '@data-api/models/metadata-query/metadata-query-response.model';
import { FilterQueryRequest } from '@data-api/models/filter-query/filter-query-request.model';
import { FilterQueryGenericResponse } from '@data-api/models/filter-query/filter-query-response.model';
import { MetricQueryRequest } from '@data-api/models/metric-query/metric-query-request.model';
import { MetricQueryGenericResponse } from '@data-api/models/metric-query/metric-query-response.model';
import { DimensionedMetricQueryRequest } from '@data-api/models/dimensioned-metric-query/dimensioned-metric-query-request.model';
import { DimensionedMetricQueryGenericResponse } from '@data-api/models/dimensioned-metric-query/dimensioned-metric-query-response.model';
import { GenericResponse } from '@data-api/models/generic-response/generic-response.model';
import { MsalTokenProviderService } from '@services/msal-token-provider.service';
import { catchError } from 'rxjs/operators';

@Injectable()
export class DataRetrieverHttpService {
  private readonly dataApiUrl: string;
  private readonly dataApiScopes: string;

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) { dataApiUrl, dataApiMsalScope }: ConfigSettings,
    private readonly msalTokenProviderService: MsalTokenProviderService
  ) {
    this.dataApiUrl = dataApiUrl;
    this.dataApiScopes = dataApiMsalScope;
  }

  private getConfig(): {
    withCredentials: boolean;
  } {
    return {
      withCredentials: true,
    };
  }

  public getMetadata(
    request: MetadataQueryRequest
  ): Observable<MetadataQueryGenericResponse> {
    return this.http
      .post<MetadataQueryGenericResponse>(
        `${this.dataApiUrl}${DATA_API_URL.metadataQuery}`,
        request,
        this.getConfig()
      )
      .pipe(
        catchError(err =>
          this.msalTokenProviderService.handleInteractionRequired(
            err,
            this.dataApiScopes
          )
        )
      );
  }

  public getFilters(
    request: FilterQueryRequest
  ): Observable<FilterQueryGenericResponse> {
    return this.http
      .post<FilterQueryGenericResponse>(
        `${this.dataApiUrl}${DATA_API_URL.filterQuery}`,
        request,
        this.getConfig()
      )
      .pipe(
        catchError(err =>
          this.msalTokenProviderService.handleInteractionRequired(
            err,
            this.dataApiScopes
          )
        )
      );
  }

  public getMetric(
    request: MetricQueryRequest
  ): Observable<MetricQueryGenericResponse> {
    return this.http
      .post<MetricQueryGenericResponse>(
        `${this.dataApiUrl}${DATA_API_URL.metricQuery}`,
        request,
        this.getConfig()
      )
      .pipe(
        catchError(err =>
          this.msalTokenProviderService.handleInteractionRequired(
            err,
            this.dataApiScopes
          )
        )
      );
  }

  public getDimensionedMetric(
    request: DimensionedMetricQueryRequest
  ): Observable<DimensionedMetricQueryGenericResponse> {
    return this.http
      .post<DimensionedMetricQueryGenericResponse>(
        `${this.dataApiUrl}${DATA_API_URL.dimensionedMetricQuery}`,
        request,
        this.getConfig()
      )
      .pipe(
        catchError(err =>
          this.msalTokenProviderService.handleInteractionRequired(
            err,
            this.dataApiScopes
          )
        )
      );
  }

  public getRefreshDate(
    dataSourceId: string
  ): Observable<GenericResponse<Date>> {
    return this.http
      .get<GenericResponse<Date>>(
        `${this.dataApiUrl}${DATA_API_URL.refreshDate}/${dataSourceId}/true`,
        { withCredentials: true }
      )
      .pipe(
        catchError(err =>
          this.msalTokenProviderService.handleInteractionRequired(
            err,
            this.dataApiScopes
          )
        )
      );
  }
}
